import React, { useState, useEffect } from "react";
import axios from "axios";
import "./PaymentQuota.css";

const PaymentQuota = ({ token }) => {
    const [offers, setOffers] = useState([]);
    const [userQuota, setUserQuota] = useState(null);
    const [loading, setLoading] = useState(true);
    const [processingPayment, setProcessingPayment] = useState(false);
    const [couponModalVisible, setCouponModalVisible] = useState(false);
    const [selectedOffer, setSelectedOffer] = useState(null);
    const [couponCode, setCouponCode] = useState("");
    const [appliedCoupon, setAppliedCoupon] = useState(null);
    const [verifyingCoupon, setVerifyingCoupon] = useState(false);

    useEffect(() => {
        fetchOffersAndQuota();
    }, []);

    const fetchOffersAndQuota = async () => {
        try {
            setLoading(true);
            const [offersRes, userRes] = await Promise.all([
                axios.get("/api/payment/offers"),
                axios.get("/api/auth/me", {
                    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
                }).catch(() => ({ data: null })),
            ]);

            if (offersRes.data.success) {
                setOffers(offersRes.data.offers);
            }

            if (userRes.data) {
                setUserQuota({
                    quotaLimit: userRes.data.quotaLimit || 0.5 * 1024 * 1024 * 1024,
                    usedQuota: userRes.data.usedQuota || 0,
                });
            }
        } catch (error) {
            console.error("Error fetching data:", error);
        } finally {
            setLoading(false);
        }
    };
    // 32212254720
    const formatBytes = (bytes) => {

        // update to show exact gb and mb 
        if (bytes === 0) return "0 GB";
        // const gb = bytes / (1024 * 1024 * 1024);
        // return gb >= 1 ? `${gb.toFixed(2)} GB` : `${(gb * 1024).toFixed(2)} MB`;
        const gb = Math.floor(bytes / (1024 * 1024 * 1024));
        const mb = Math.floor((bytes % (1024 * 1024 * 1024)) / (1024 * 1024));
        let result = "";
        if (gb > 0) result += `${gb} GB`;
        if (mb > 0) result += gb > 0 ? ` ${mb} MB` : `${mb} MB`;
        return result.trim();
    };

    const getQuotaPercentage = () => {
        if (!userQuota) return 0;
        return Math.min((userQuota.usedQuota / userQuota.quotaLimit) * 100, 100);
    };

    const calculateDiscount = (originalPrice, discountedPrice) => {
        const discount = ((originalPrice - discountedPrice) / originalPrice) * 100;
        return Math.round(discount);
    };

    // const getOriginalPrice = (price) => {
    //     // Calculate original price assuming 99% discount
    //     return Math.round(price * 100);
    // };

    const openPaymentModal = (offer) => {
        setSelectedOffer(offer);
        setCouponCode("");
        setAppliedCoupon(null);
        setCouponModalVisible(true);
    };

    const verifyCoupon = async () => {
        if (!couponCode.trim()) return;
        try {
            setVerifyingCoupon(true);
            const token = localStorage.getItem("token");
            const res = await axios.post(
                "/api/payment/verify-coupon",
                { code: couponCode, offerId: selectedOffer._id },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (res.data.success) {
                setAppliedCoupon(res.data);
                alert("Coupon applied successfully!");
            }
        } catch (error) {
            alert(error.response?.data?.error || "Invalid coupon");
            setAppliedCoupon(null);
        } finally {
            setVerifyingCoupon(false);
        }
    };

    const handlePayment = async () => {
        if (!selectedOffer) return;
        if (!window.Razorpay) {
            alert("Razorpay SDK not loaded. Please refresh the page.");
            return;
        }

        try {
            setProcessingPayment(true);
            const token = localStorage.getItem("token");

            const orderRes = await axios.post(
                "/api/payment/create-order",
                { offerId: selectedOffer._id, couponCode: appliedCoupon ? couponCode : null },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            const { order, key, freeOrder } = orderRes.data;

            if (freeOrder) {
                alert("🎉 Success! Your storage quota has been upgraded for free!");
                fetchOffersAndQuota();
                setCouponModalVisible(false);
                setProcessingPayment(false);
                return;
            }

            const options = {
                key: key,
                image: "https://server.goldenmemory108.in/static/media/logo1.fb5469b70ab179be03f2.jpg",
                amount: order.amount,
                currency: order.currency,
                name: "Golden Memory",
                description: selectedOffer.description,
                order_id: order.id,
                handler: async function (response) {
                    try {
                        const verifyRes = await axios.post(
                            "/api/payment/verify-payment",
                            {
                                razorpay_order_id: response.razorpay_order_id,
                                razorpay_payment_id: response.razorpay_payment_id,
                                razorpay_signature: response.razorpay_signature,
                            },
                            { headers: { Authorization: `Bearer ${token}` } }
                        );

                        if (verifyRes.data.success) {
                            alert("🎉 Payment successful! Your storage quota has been upgraded!");
                            fetchOffersAndQuota();
                            setCouponModalVisible(false);
                        }
                    } catch (error) {
                        console.error("Payment verification failed:", error);
                        alert("Payment verification failed. Please contact support.");
                    } finally {
                        setProcessingPayment(false);
                    }
                },
                prefill: { name: "", email: "", contact: "" },
                theme: { color: "#667eea" },
                modal: {
                    ondismiss: function () {
                        setProcessingPayment(false);
                    },
                },
            };

            const rzp = new window.Razorpay(options);
            rzp.open();
        } catch (error) {
            console.error("Payment initiation failed:", error);
            alert("Failed to initiate payment. Please try again.");
            setProcessingPayment(false);
        }
    };

    if (loading) {
        return (
            <div className="payment-quota-loading">
                <div className="spinner"></div>
                <p>Loading offers...</p>
            </div>
        );
    }

    return (
        <div className="payment-quota-container">
            {/* Quota Display */}
            {userQuota && (
                <div className="quota-display">
                    <div className="quota-header">
                        <div className="quota-icon">☁️</div>
                        <h3>Storage Quota</h3>
                    </div>
                    <div className="quota-stats-grid">
                        <div className="quota-stat">
                            <span className="quota-stat-label">Used</span>
                            <span className="quota-stat-value">{formatBytes(userQuota.usedQuota)}</span>
                        </div>
                        <div className="quota-stat">
                            <span className="quota-stat-label">Total</span>
                            <span className="quota-stat-value">{formatBytes(userQuota.quotaLimit)}</span>
                        </div>
                        <div className="quota-stat">
                            <span className="quota-stat-label">Available</span>
                            <span className="quota-stat-value">
                                {formatBytes(userQuota.quotaLimit - userQuota.usedQuota)}
                            </span>
                        </div>
                    </div>
                    <div className="quota-bar-container">
                        <div className="quota-bar">
                            <div
                                className="quota-bar-fill"
                                style={{
                                    width: `${getQuotaPercentage()}%`,
                                    backgroundColor:
                                        getQuotaPercentage() > 90
                                            ? "#ff5252"
                                            : getQuotaPercentage() > 70
                                                ? "#ffa726"
                                                : "#66bb6a",
                                }}
                            ></div>
                        </div>
                        <span className="quota-percentage">{getQuotaPercentage().toFixed(1)}%</span>
                    </div>
                </div>
            )}

            {/* Offers Section */}
            {offers.length > 0 && (
                <div className="offers-section">
                    <div className="offers-header">
                        <h3>🎁 Special Offers</h3>
                        <div className="discount-badge-header">99% OFF</div>
                    </div>
                    <p className="offers-subtitle">Limited time offer - Upgrade your storage now!</p>

                    <div className="offers-grid">
                        {offers.map((offer, index) => {
                            const originalPrice = offer.originalPrice;//getOriginalPrice(offer.price);
                            const discount = calculateDiscount(originalPrice, offer.price);
                            const isSpecial = offer.type === "special";

                            return (
                                <div
                                    key={offer._id}
                                    className={`offer-card ${isSpecial ? "offer-card-special" : ""} offer-card-${index % 3}`}
                                >
                                    {isSpecial && (
                                        <div className="special-ribbon">
                                            <span>SPECIAL</span>
                                        </div>
                                    )}

                                    <div className="offer-card-header">
                                        <h4>{offer.title}</h4>
                                        <div className="discount-tag">{discount}% OFF</div>
                                    </div>

                                    <p className="offer-description">{offer.description}</p>

                                    <div className="price-section">
                                        <div className="price-row">
                                            <span className="original-price">₹{originalPrice}</span>
                                        </div>
                                        <div className="final-price-row">
                                            <span className="final-price">₹{offer.price}</span>
                                            <span className="price-period">
                                                / {offer.validityDays >= 365 ? `${Math.floor(offer.validityDays / 365)} year` : `${offer.validityDays} days`}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="storage-badge">
                                        <span className="storage-icon">💾</span>
                                        <span>+{formatBytes(offer.quotaLimit)} Storage</span>
                                    </div>

                                    {offer.features && offer.features.length > 0 && (
                                        <ul className="offer-features">
                                            {offer.features.map((feature, idx) => (
                                                <li key={idx}>
                                                    <span className="feature-check">✓</span>
                                                    {feature}
                                                </li>
                                            ))}
                                        </ul>
                                    )}

                                    <button
                                        className="pay-button"
                                        onClick={() => openPaymentModal(offer)}
                                        disabled={processingPayment}
                                    >
                                        {processingPayment ? (
                                            <span className="spinner-small"></span>
                                        ) : (
                                            <>
                                                <span className="pay-icon">💳</span>
                                                Pay ₹{offer.price}
                                            </>
                                        )}
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Payment & Coupon Modal */}
            {couponModalVisible && selectedOffer && (
                <div className="coupon-modal-overlay">
                    <div className="coupon-modal-content">
                        <div className="coupon-modal-header">
                            <h2>Checkout</h2>
                            <button className="coupon-close-btn" onClick={() => setCouponModalVisible(false)}>×</button>
                        </div>

                        <div className="coupon-checkout-details">
                            <h3 className="coupon-offer-title">{selectedOffer.title}</h3>
                            <p className="coupon-offer-desc">{selectedOffer.description}</p>

                            <div className="coupon-price-breakdown">
                                <div className="coupon-price-row">
                                    <span className="coupon-price-label">Original Price</span>
                                    <span className="coupon-price-value">₹{selectedOffer.price}</span>
                                </div>

                                {appliedCoupon && (
                                    <div className="coupon-price-row discount-row">
                                        <span className="coupon-price-label">Discount ({appliedCoupon.discountPercentage}%)</span>
                                        <span className="coupon-price-value">- ₹{appliedCoupon.discountAmount}</span>
                                    </div>
                                )}

                                <div className="coupon-divider"></div>

                                <div className="coupon-price-row total-row">
                                    <span className="coupon-total-label">Total to Pay</span>
                                    <span className="coupon-total-value">
                                        ₹{appliedCoupon ? appliedCoupon.finalPrice : selectedOffer.price}
                                    </span>
                                </div>
                            </div>

                            <label className="coupon-input-label">Have a coupon code?</label>
                            <div className="coupon-input-group">
                                <input
                                    type="text"
                                    placeholder="Enter code"
                                    value={couponCode}
                                    onChange={(e) => {
                                        setCouponCode(e.target.value.toUpperCase());
                                        setAppliedCoupon(null);
                                    }}
                                />
                                <button 
                                    className="coupon-apply-btn"
                                    onClick={verifyCoupon}
                                    disabled={!couponCode || verifyingCoupon}
                                >
                                    {verifyingCoupon ? "..." : "Apply"}
                                </button>
                            </div>

                            <button
                                className="coupon-checkout-btn"
                                onClick={handlePayment}
                                disabled={processingPayment}
                            >
                                {processingPayment ? (
                                    <span className="spinner-small"></span>
                                ) : (
                                    `Proceed to Pay ₹${appliedCoupon ? appliedCoupon.finalPrice : selectedOffer.price}`
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PaymentQuota;
